import { createCappedStackVariantComponent } from 'components/capped-stack';
import type {
	ComponentContent,
	ComponentSlot,
	ComponentValue,
	Dialog,
	DialogProps,
} from 'store/ui';
import {
	isRef,
	unref,
} from 'vue';

interface GlobalUIVisibleItem {
	visible?: ComponentValue<boolean>;
}

const DIALOGS_CONTAINER_ID = 'dialogs';
const FLOATING_ALERTS_CONTAINER_ID = 'floating-alerts';
const MAX_VISIBLE_DIALOGS = 1;
const MAX_VISIBLE_FLOATING_ALERTS = 2;
const MAX_VISIBLE_TOASTS = 3;
const TOASTS_CONTAINER_ID = 'toasts';

const DialogStack = createCappedStackVariantComponent(
	'DialogStack',
	MAX_VISIBLE_DIALOGS,
	{
		preserveHiddenItems: true,
	},
);
const FloatingAlertStack = createCappedStackVariantComponent(
	'FloatingAlertStack',
	MAX_VISIBLE_FLOATING_ALERTS,
	{
		preserveHiddenItems: true,
	},
);
const ToastStack = createCappedStackVariantComponent(
	'ToastStack',
	MAX_VISIBLE_TOASTS,
	{
		preserveHiddenItems: true,
	},
);

/**
 * Resolves whether a covered dialog should remain visible as noninteractive context.
 *
 * @param dialog Global dialog that may opt into being retained beneath another dialog.
 * @returns Whether the dialog explicitly requests underlay visibility.
 */
function isGlobalDialogUnderlay(dialog: Dialog): boolean {
	return resolveComponentValue(dialog.keepVisibleWhenCovered) === true;
}

/**
 * Resolves whether a global UI item should be eligible for stack visibility.
 *
 * @param item - Global UI item that may expose a reactive `visible` flag.
 * @returns True unless the item explicitly resolves `visible` to false.
 */
function isGlobalUIItemVisible(item: GlobalUIVisibleItem): boolean {
	return resolveComponentValue(item.visible) !== false;
}

/**
 * Normalizes component content into a renderable slot list.
 *
 * @param content - Component content that may be a plain value, ref, or computed ref.
 * @returns The normalized component slots.
 */
function normalizeComponentSlots(content: ComponentContent | ComponentSlot['slot'] | undefined): ComponentSlot[] {
	const resolved = resolveComponentValue(content);

	if (
		!resolved
		|| typeof resolved === 'function'
		|| typeof resolved === 'string'
	) {
		return [];
	}

	const slots = (
		Array.isArray(resolved)
			? resolved
			: [resolved]
	);

	return slots.map((slot) => unref(slot) as ComponentSlot);
}

/**
 * Resolves text slot content from a component slot definition.
 *
 * @param slot - Component slot content that may be a plain value, ref, or computed ref.
 * @returns Text content when the slot resolves to a string.
 */
function resolveComponentTextSlot(slot: ComponentSlot['slot']): string | undefined {
	const resolved = resolveComponentValue(slot);

	return typeof resolved === 'string'
		? resolved
		: undefined;
}

/**
 * Resolves a plain value, ref, or computed ref without invoking function props.
 *
 * @param value - The value to resolve.
 * @returns The unwrapped value.
 */
function resolveComponentValue<T>(value: ComponentValue<T> | undefined): T | undefined {
	if (typeof value === 'undefined') {
		return undefined;
	}

	let resolvedValue: unknown = value;

	while (isRef(resolvedValue)) {
		resolvedValue = unref(resolvedValue);
	}

	return resolvedValue as T;
}

/**
 * Suppresses covered dialog handlers, including document-level outside-click callbacks.
 *
 * @param dialog Global dialog whose shell props are being rendered.
 * @param isInteractive Whether this dialog occupies an interactive stack position.
 * @returns Shell props with dismissal listeners enabled only for an interactive dialog.
 */
function resolveGlobalDialogProps(dialog: Dialog, isInteractive: boolean): DialogProps | undefined {
	const props = resolveComponentValue(dialog.props);

	if (isInteractive || !props) {
		return props;
	}

	return {
		...props,
		'onClick:outside': undefined,
		onClose: undefined,
	};
}

export {
	DIALOGS_CONTAINER_ID,
	FLOATING_ALERTS_CONTAINER_ID,
	MAX_VISIBLE_DIALOGS,
	MAX_VISIBLE_FLOATING_ALERTS,
	MAX_VISIBLE_TOASTS,
	TOASTS_CONTAINER_ID,

	DialogStack,
	FloatingAlertStack,
	ToastStack,

	isGlobalDialogUnderlay,
	isGlobalUIItemVisible,
	normalizeComponentSlots,
	resolveComponentTextSlot,
	resolveComponentValue,
	resolveGlobalDialogProps,
};
